import { EcommercePayment, OrderAddress } from "../requests/OrderRequests";
import {
    CountryAbbreviationForId,
    StateAbbreviationForId,
} from "./addressFormSelections";

const SONGBIRD_SCRIPT_ID = "songbirdScript";
const NUM_CHARACTERS_FOR_REDUCED_CART_KEY = 24;

const stagingSongbirdConfig = {
    timeout: 10000,
    maxRequestRetries: 2,
    logging: {
        level: "on",
    },
};

const productionSongbirdConfig = {
    timeout: 10000,
    maxRequestRetries: 2,
    logging: {
        level: "off",
    },
};

//nb: "onLoad" should be something like this.setState({cardinalReady: true})
export const LoadSongbirdScript = (
    scriptSrc: string,
    env: string,
    token: string,
    onLoad: () => void,
    onProcessed: (
        sendToBackend: boolean,
        prompt: string,
        token: string,
    ) => void,
    onLoadError: (prompt: string | false) => void,
) => {
    const existingScript = document.getElementById(SONGBIRD_SCRIPT_ID);

    if (!existingScript) {
        const script = document.createElement("script");
        script.src = scriptSrc;
        script.id = SONGBIRD_SCRIPT_ID;
        script.type = "text/javascript";
        script.charset = "utf-8";

        script.onerror = (e: any) => {
            console.error(
                "Could not load Songbird.js: cannot process payments.",
            );
            onLoadError(false);
        };
        script.onload = (e: any) => {
            const cardinal = (window as any).Cardinal;
            if (!cardinal) {
                //this should never happen
                console.error(
                    "Songbird.js did not load properly; cannot process payments.",
                );
                onLoadError(false);
                return;
            }
            cardinal.configure(
                env.toLowerCase() !== "production"
                    ? stagingSongbirdConfig
                    : productionSongbirdConfig,
            );
            cardinal.on("payments.setupComplete", onLoad);
            cardinal.on("payments.validated", (data: any, jwt: any) => {
                //For ANY case where we receive a JWT back, send it to our backend for validation/logging
                if (!jwt) {
                    onProcessed(
                        false,
                        "Could not validate payment information, please try again. If this issue persists, please contact support.",
                        jwt,
                    );
                } else {
                    onProcessed(true, "", jwt);
                }
            });
            cardinal.setup("init", { jwt: token });
        };
        document.head.appendChild(script);
    } else {
        //if the script is already present, that means we've been to the checkout page already, and the cart could have changed between then and now.
        //TODO: How do we handle changes to the Order? update the jwt in place here? or do it in the Cardinal.start step?
        const cardinal = (window as any).Cardinal;
        if (!cardinal) {
            //this should never happen
            console.error(
                "Songbird.js did not load properly; cannot process payments.",
            );
            onLoadError(false);
            return;
        }
        cardinal.trigger("jwt.update", token);
        onLoad();
    }
};

export const StartCardinalTransaction = async (
    orderNumber: string,
    address: OrderAddress,
    payment: EcommercePayment,
) => {
    const cardinal = (window as any).Cardinal;
    if (cardinal === null || cardinal === undefined) {
        //this should never happen
        console.log(
            "Songbird.js did not load properly; cannot process payments.",
        );
        return;
    }
    const cardinalAddress = {
        FirstName: address.firstName,
        LastName: address.lastName,
        Address1: address.address,
        Address2: address.address2 !== undefined ? address.address2 : "",
        City: address.city,
        State: StateAbbreviationForId(address.stateId),
        CountryCode: CountryAbbreviationForId(address.countryId),
        PostalCode: address.postal,
        Phone1: address.phone,
    };
    const order = {
        Consumer: {
            Email1: address.email,
            BillingAddress: cardinalAddress,
            ShippingAddress: cardinalAddress,
            Account: {
                AccountNumber: payment.cardNum,
                ExpirationMonth: payment.expiryMonth,
                ExpirationYear: "" + (2000 + parseInt(payment.expiryYear!)),
                CardCode: payment.cvv,
                NameOnAccount: payment.nameOnCard,
            },
        },
        OrderDetails: {
            OrderNumber: orderNumber.slice(
                0,
                NUM_CHARACTERS_FOR_REDUCED_CART_KEY,
            ),
        },
    };

    // BIN Profiling
    try {
        const binResponse = await cardinal.trigger(
            "bin.process",
            payment.cardNum,
        );
        if (!binResponse || !binResponse.Status) {
            console.warn("BIN Processing Error (A)", binResponse);
            // TODO: Maybe we just log and ignore this?
            // throw new Error("We were unable to verify your credit card number. Please double check it and try again. (A)");
        }
    } catch (e) {
        console.warn("BIN Processing Error (B)", e);
        throw new Error(
            "We were unable to verify your credit card number. Please double check it and try again. (B)",
        );
    }

    cardinal.start("cca", order);
};

export const UpdateJwt = async (token: string) => {
    const cardinal = (window as any).Cardinal;
    if (!cardinal) {
        //this should never happen
        console.error(
            "Songbird.js did not load properly; cannot process payments.",
        );
        return;
    }
    await cardinal.trigger("jwt.update", token);
};
